import React, { useEffect, useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useLessonList } from '@/hooks/queries/lesson/useList';
import { useLessonCreate, useLessonUpdate, useLessonDelete } from '@/hooks/mutations/lesson';
import { Lesson, LessonInput } from '@/types/lessons';
import { useTestList } from '@/hooks/queries/test/useList';
import { useTestCreate, useTestUpdate, useTestDelete } from '@/hooks/mutations/test';
import { Test, TestInput } from '@/types/tests';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, Video, Globe, Lock, PlayCircle, HelpCircle, ChevronRight } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/utils/styleUtils';
import { TestQuestionsModal } from './TestQuestionsModal';
import { Pagination } from '@/components/Pagination';

interface LessonTestPanelProps {
  courseId: string;
  moduleId: string;
}

export const LessonTestPanel = ({ courseId, moduleId }: LessonTestPanelProps) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('lessons');
  const [lessonsPage, setLessonsPage] = useState(1);
  const [testsPage, setTestsPage] = useState(1);

  useEffect(() => {
    setLessonsPage(1);
    setTestsPage(1);
  }, [moduleId]);

  // Lesson State
  const [isLessonModalOpen, setIsLessonModalOpen] = useState(false);
  const [isLessonDeleteOpen, setIsLessonDeleteOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [lessonFormData, setLessonFormData] = useState<LessonInput>({
    moduleId: moduleId,
    courseId: courseId,
    duration: 30,
    isPublic: false,
    videos: [{ link: '', linkType: 'YOUTUBE' }],
    translations: [
      { locale: 'uz', title: '', blocks: [] },
      { locale: 'ru', title: '', blocks: [] },
      { locale: 'en', title: '', blocks: [] },
    ],
  });

  // Test State
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [isTestDeleteOpen, setIsTestDeleteOpen] = useState(false);
  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState<Test | null>(null);
  const [testFormData, setTestFormData] = useState<TestInput>({
    moduleId: moduleId,
    passingPercentage: 70,
    maxAttempts: 3,
    timeLimit: 30,
    shuffleQuestions: true,
    shuffleAnswers: true,
    translations: [
      { locale: 'uz', name: '' },
      { locale: 'ru', name: '' },
      { locale: 'en', name: '' },
    ],
  });

  // Queries
  const { data: lessonsData, isLoading: isLoadingLessons, refetch: refetchLessons } = useLessonList({
    courseId,
    moduleId,
    pageNumber: lessonsPage,
    pageSize: 20,
  });

  const { data: testsData, isLoading: isLoadingTests, refetch: refetchTests } = useTestList({
    moduleId,
    pageNumber: testsPage,
    pageSize: 10,
  });

  const lessonsList: Lesson[] = lessonsData?.data?.data || lessonsData?.data || [];
  const testsList: Test[] = testsData?.data?.data || testsData?.data || [];
  const currentTest = testsList[0]; // A module has only 1 test usually

  // Mutations (Lesson)
  const { mutate: createLesson, isPending: isCreatingLesson } = useLessonCreate();
  const { mutate: updateLesson, isPending: isUpdatingLesson } = useLessonUpdate();
  const { mutate: deleteLesson } = useLessonDelete();

  // Mutations (Test)
  const { mutate: createTest, isPending: isCreatingTest } = useTestCreate();
  const { mutate: updateTest, isPending: isUpdatingTest } = useTestUpdate();
  const { mutate: deleteTest } = useTestDelete();

  // --- LESSON HANDLERS ---
  const handleOpenLessonCreate = () => {
    setSelectedLesson(null);
    setLessonFormData({
      moduleId,
      courseId,
      duration: 30,
      isPublic: false,
      videos: [{ link: '', linkType: 'YOUTUBE' }],
      translations: [
        { locale: 'uz', title: '', blocks: [] },
        { locale: 'ru', title: '', blocks: [] },
        { locale: 'en', title: '', blocks: [] },
      ],
    });
    setIsLessonModalOpen(true);
  };

  const handleOpenLessonEdit = (lesson: Lesson) => {
    setSelectedLesson(lesson);
    setLessonFormData({
      moduleId: lesson.moduleId,
      courseId: lesson.courseId,
      duration: lesson.duration || 30,
      isPublic: lesson.isPublic,
      videos: lesson.videos?.length ? lesson.videos.map((v) => ({ link: v.link, linkType: v.linkType })) : [{ link: '', linkType: 'YOUTUBE' }],
      translations: [
        { locale: 'uz', title: lesson.title || '', blocks: lesson.blocks || [] },
        { locale: 'ru', title: '', blocks: [] },
        { locale: 'en', title: '', blocks: [] },
      ],
    });
    setIsLessonModalOpen(true);
  };

  const handleLessonTranslationChange = (locale: 'uz' | 'ru' | 'en', value: string) => {
    setLessonFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, title: value } : t)),
    }));
  };

  const handleLessonVideoChange = (link: string) => {
    setLessonFormData((prev) => ({
      ...prev,
      videos: prev.videos.map((v, i) => (i === 0 ? { ...v, link } : v)),
    }));
  };

  const submitLesson = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanVideos = lessonFormData.videos.filter((v) => v.link.trim() !== '');
    if (selectedLesson) {
      updateLesson(
        {
          id: selectedLesson.id,
          data: {
            duration: lessonFormData.duration,
            isPublic: lessonFormData.isPublic,
            videos: cleanVideos,
            translations: lessonFormData.translations,
          },
        },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Dars yangilandi' });
            setIsLessonModalOpen(false);
            refetchLessons();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Dars yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createLesson(
        { ...lessonFormData, courseId, moduleId, videos: cleanVideos },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Yangi dars yaratildi' });
            setIsLessonModalOpen(false);
            refetchLessons();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Dars yaratilmadi', variant: 'destructive' }),
        }
      );
    }
  };

  const doDeleteLesson = () => {
    if (!selectedLesson) return;
    deleteLesson(selectedLesson.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Dars o'chirildi" });
        setIsLessonDeleteOpen(false);
        setSelectedLesson(null);
        refetchLessons();
      },
      onError: () => toast({ title: 'Xatolik', description: "Dars o'chirilmadi", variant: 'destructive' }),
    });
  };

  // --- TEST HANDLERS ---
  const handleOpenTestCreate = () => {
    setSelectedTest(null);
    setTestFormData({
      moduleId,
      passingPercentage: 70,
      maxAttempts: 3,
      timeLimit: 30,
      shuffleQuestions: true,
      shuffleAnswers: true,
      translations: [
        { locale: 'uz', name: '' },
        { locale: 'ru', name: '' },
        { locale: 'en', name: '' },
      ],
    });
    setIsTestModalOpen(true);
  };

  const handleOpenTestEdit = (test: Test) => {
    setSelectedTest(test);
    setTestFormData({
      moduleId: test.moduleId,
      passingPercentage: test.passingPercentage,
      maxAttempts: test.maxAttempts,
      timeLimit: test.timeLimit,
      shuffleQuestions: test.shuffleQuestions,
      shuffleAnswers: test.shuffleAnswers,
      translations: [
        { locale: 'uz', name: test.name || '' },
        { locale: 'ru', name: '' },
        { locale: 'en', name: '' },
      ],
    });
    setIsTestModalOpen(true);
  };

  const handleTestTranslationChange = (locale: 'uz' | 'ru' | 'en', value: string) => {
    setTestFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, name: value } : t)),
    }));
  };

  const submitTest = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTest) {
      updateTest(
        {
          id: selectedTest.id,
          data: {
            passingPercentage: testFormData.passingPercentage,
            translations: testFormData.translations,
          },
        },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Test yangilandi' });
            setIsTestModalOpen(false);
            refetchTests();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Test yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createTest(
        { ...testFormData, moduleId },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Modul testi yaratildi' });
            setIsTestModalOpen(false);
            refetchTests();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Test yaratilmadi', variant: 'destructive' }),
        }
      );
    }
  };

  const doDeleteTest = () => {
    if (!selectedTest) return;
    deleteTest(selectedTest.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Test o'chirildi" });
        setIsTestDeleteOpen(false);
        setSelectedTest(null);
        refetchTests();
      },
      onError: () => toast({ title: 'Xatolik', description: "Test o'chirilmadi", variant: 'destructive' }),
    });
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
      <div className="p-4 border-b border-border bg-slate-50/50 flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div>
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <PlayCircle className="size-4 text-primary" />
            Darslar va Test
          </h2>
          <p className="text-xs text-slate-500 mt-1">Tanlangan modul tarkibi</p>
        </div>
      </div>

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col h-full">
          <div className="px-4 pt-3 border-b border-slate-100 shrink-0">
            <TabsList className="grid w-full max-w-sm grid-cols-2 bg-slate-100 p-1 rounded-xl">
              <TabsTrigger value="lessons" className="rounded-lg text-xs font-medium gap-2">
                <PlayCircle className="size-3.5" /> Darslar
              </TabsTrigger>
              <TabsTrigger value="test" className="rounded-lg text-xs font-medium gap-2">
                <HelpCircle className="size-3.5" /> Modul Testi
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
            <TabsContent value="lessons" className="m-0 h-full">
              {isLoadingLessons ? (
                <div className="py-12"><Loader /></div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <Button size="sm" onClick={handleOpenLessonCreate} className="h-8 gap-1.5 px-3 rounded-lg bg-primary text-white hover:bg-primary/90">
                      <Plus className="size-3.5" /> Yangi dars
                    </Button>
                  </div>

                  {lessonsList.length === 0 ? (
                    <div className="text-center py-12 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                      <PlayCircle className="size-10 mx-auto text-slate-300 mb-3" />
                      <p className="text-sm font-medium text-slate-600">Darslar topilmadi</p>
                      <p className="text-xs mt-1">Bu modulda hali darslar yaratilmagan</p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {lessonsList.map((lesson) => (
                        <div key={lesson.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-all duration-200 group">
                          <div className="flex items-center gap-3">
                            <span className="flex items-center justify-center size-7 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold shrink-0">
                              {lesson.orderIndex || 1}
                            </span>
                            <div>
                              <p className="font-semibold text-slate-900 text-sm">{lesson.title || 'Nomsiz dars'}</p>
                              <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500 font-medium">
                                <span className="flex items-center gap-1">
                                  <Video className="size-3" /> {lesson.duration || 30} daq
                                </span>
                                <span className="flex items-center gap-1">
                                  {lesson.isPublic ? <Globe className="size-3 text-emerald-500" /> : <Lock className="size-3" />}
                                  {lesson.isPublic ? <span className="text-emerald-600">Ommaviy</span> : 'Yopiq'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" onClick={() => handleOpenLessonEdit(lesson)} className="size-8 text-slate-500 hover:text-primary">
                              <Edit2 className="size-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedLesson(lesson); setIsLessonDeleteOpen(true); }} className="size-8 text-slate-500 hover:text-red-500">
                              <Trash2 className="size-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
<Pagination currentPage={lessonsPage} setCurrentPage={setLessonsPage} paginationInfo={lessonsData?.meta?.pagination ?? lessonsData?.data?.meta?.pagination} />
                </div>
              )}
            </TabsContent>

            <TabsContent value="test" className="m-0 h-full">
              {isLoadingTests ? (
                <div className="py-12"><Loader /></div>
              ) : (
                <div className="space-y-4">
                  {!currentTest ? (
                    <div className="text-center py-12 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                      <HelpCircle className="size-10 mx-auto text-slate-300 mb-3" />
                      <p className="text-sm font-medium text-slate-600">Test mavjud emas</p>
                      <p className="text-xs mt-1 mb-4">Ushbu modul uchun yakuniy test yaratilmagan</p>
                      <Button size="sm" onClick={handleOpenTestCreate} className="h-8 gap-1.5 px-4 rounded-lg bg-primary text-white hover:bg-primary/90">
                        <Plus className="size-3.5" /> Test yaratish
                      </Button>
                    </div>
                  ) : (
                    <div className="bg-white border border-slate-200 p-5 rounded-xl space-y-4">
                      <div className="flex items-start justify-between border-b border-slate-100 pb-4">
                        <div>
                          <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 mb-2 border-0">Oraliq Test</Badge>
                          <h3 className="font-bold text-lg text-slate-900">{currentTest.name || 'Nomsiz test'}</h3>
                          <p className="text-xs font-mono text-slate-400 mt-1">{currentTest.id}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleOpenTestEdit(currentTest)} className="h-8 gap-1 text-xs text-slate-600">
                            <Edit2 className="size-3.5" /> Tahrirlash
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setSelectedTest(currentTest); setIsTestDeleteOpen(true); }} className="h-8 gap-1 text-xs text-red-600 hover:bg-red-50">
                            <Trash2 className="size-3.5" /> O'chirish
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm pt-2">
                        <div>
                          <p className="text-slate-500 text-xs font-medium">O'tish balii</p>
                          <p className="font-semibold text-emerald-600 mt-0.5">{currentTest.passingPercentage}%</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs font-medium">Vaqt limiti</p>
                          <p className="font-semibold text-slate-900 mt-0.5">{currentTest.timeLimit} daqiqa</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs font-medium">Urinishlar soni</p>
                          <p className="font-semibold text-slate-900 mt-0.5">{currentTest.maxAttempts} marta</p>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-slate-100">
                        <Button 
                          variant="outline" 
                          className="w-full justify-between group"
                          onClick={() => setIsQuestionsModalOpen(true)}
                        >
                          Savollarni boshqarish
                          <ChevronRight className="size-4 text-slate-400 group-hover:text-primary transition-colors" />
                        </Button>
                      </div>
                    </div>
                  )}
<Pagination currentPage={testsPage} setCurrentPage={setTestsPage} paginationInfo={testsData?.meta?.pagination ?? testsData?.data?.meta?.pagination} />
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* Lesson Modals */}
      <Dialog open={isLessonModalOpen} onOpenChange={setIsLessonModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedLesson ? 'Darsni tahrirlash' : 'Yangi dars yaratish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={submitLesson} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Davomiyligi (daqiqa)</Label>
                <Input
                  type="number"
                  value={lessonFormData.duration}
                  onChange={(e) => setLessonFormData({ ...lessonFormData, duration: Number(e.target.value) })}
                  className="rounded-xl"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Video havola (YouTube / MP4)</Label>
                <Input
                  placeholder="https://youtube.com/..."
                  value={lessonFormData.videos[0]?.link || ''}
                  onChange={(e) => handleLessonVideoChange(e.target.value)}
                  className="rounded-xl text-xs"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div>
                <Label className="text-sm font-semibold text-slate-800">Ommaviy bepul dars (isPublic)</Label>
                <p className="text-xs text-slate-500">Ro'yxatdan o'tmagan mehmonlar ham ko'ra oladi</p>
              </div>
              <Switch
                checked={lessonFormData.isPublic}
                onCheckedChange={(checked) => setLessonFormData({ ...lessonFormData, isPublic: checked })}
              />
            </div>

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli dars nomlari</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = lessonFormData.translations.find((t) => t.locale === locale) || { title: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Dars sarlavhasi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.title || ''}
                          onChange={(e) => handleLessonTranslationChange(locale, e.target.value)}
                          placeholder="HTML Asoslari va Teglar"
                          className="rounded-xl"
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsLessonModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreatingLesson || isUpdatingLesson} className="rounded-xl bg-primary text-white">
                {isCreatingLesson || isUpdatingLesson ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isLessonDeleteOpen}
        setIsOpen={setIsLessonDeleteOpen}
        alertTitle="Darsni o'chirish"
        alertDescription="Haqiqatan ham darsni o'chirmoqchimisiz?"
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={doDeleteLesson}
      />

      {/* Test Modals */}
      <Dialog open={isTestModalOpen} onOpenChange={setIsTestModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedTest ? 'Testni tahrirlash' : 'Yangi modul testi'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={submitTest} className="space-y-4 mt-2">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">O'tish foizi (%)</Label>
                <Input
                  type="number"
                  value={testFormData.passingPercentage}
                  onChange={(e) => setTestFormData({ ...testFormData, passingPercentage: Number(e.target.value) })}
                  className="rounded-xl"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Vaqt (daqiqa)</Label>
                <Input
                  type="number"
                  value={testFormData.timeLimit || 30}
                  onChange={(e) => setTestFormData({ ...testFormData, timeLimit: Number(e.target.value) })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Max urinishlar</Label>
                <Input
                  type="number"
                  value={testFormData.maxAttempts || 3}
                  onChange={(e) => setTestFormData({ ...testFormData, maxAttempts: Number(e.target.value) })}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli nomlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = testFormData.translations.find((t) => t.locale === locale) || { name: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Test nomi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.name || ''}
                          onChange={(e) => handleTestTranslationChange(locale, e.target.value)}
                          placeholder="Modul yakuniy testi"
                          className="rounded-xl"
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsTestModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreatingTest || isUpdatingTest} className="rounded-xl bg-primary text-white">
                {isCreatingTest || isUpdatingTest ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isTestDeleteOpen}
        setIsOpen={setIsTestDeleteOpen}
        alertTitle="Testni o'chirish"
        alertDescription="Ushbu test o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={doDeleteTest}
      />

      {currentTest && (
        <TestQuestionsModal
          isOpen={isQuestionsModalOpen}
          onClose={() => setIsQuestionsModalOpen(false)}
          testId={currentTest.id}
        />
      )}
    </div>
  );
};
